import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaSearch, FaTimes, FaChevronDown } from "react-icons/fa";
import { AnimatePresence } from "framer-motion";
import topics from "../Data/topicslist.jsx";
import SEO, { SITE_NAME } from "../../../Components/SEO.jsx";
import ActivityCard from "../../../Components/cards/ActivityCard";
import { toCardModel } from "../../../Components/cards/cardModel";
import './topics.css';

const FilterSelect = ({ label, options, value, onChange, placeholder = `Select ${label}...` }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const hasValue = value.length > 0;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (triggerRef.current && !triggerRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOptionClick = (option) => {
    onChange(option);
  };

  const handleChipRemove = (option, e) => {
    e.stopPropagation();
    onChange(option);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
    if (e.key === "ArrowDown" && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  return (
    <div className="filter-select" ref={selectRef}>
      <button
        type="button"
        className="filter-select__trigger"
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${label} filter${hasValue ? `, ${value.length} selected` : ''}`}
        data-has-value={hasValue}
      >
        <span className={hasValue ? "filter-select__selected" : "filter-select__placeholder"}>
          {hasValue ? (
            value.map(v => (
              <span key={v} className="filter-select__chip">
                {v}
                <button
                  type="button"
                  className="filter-select__chip-remove"
                  onClick={(e) => handleChipRemove(v, e)}
                  aria-label={`Remove ${v}`}
                >
                  ×
                </button>
              </span>
            ))
          ) : (
            placeholder
          )}
        </span>
        <FaChevronDown className="filter-select__icon" aria-hidden="true" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <div className="filter-select__dropdown" ref={dropdownRef} role="listbox" aria-label={`${label} options`}>
            {options.map((option) => (
              <button
                key={option}
                type="button"
                className="filter-select__option"
                role="option"
                aria-selected={value.includes(option)}
                aria-checked={value.includes(option)}
                onClick={() => handleOptionClick(option)}
              >
                <span className="filter-select__checkbox" aria-hidden="true" />
                <span>{option}</span>
              </button>
            ))}
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TopicCard = ({ topic }) => {
  const model = toCardModel(topic, {
    to: `/lesson-plans/${topic.id}`,
    cta: 'Start Lesson',
  });

  return (
    <li>
      <ActivityCard {...model} />
    </li>
  );
};

const EmptyState = ({ onClearFilters }) => (
  <div className="empty-state" role="status">
    <div className="empty-state__icon" aria-hidden="true">
      <FaTimes />
    </div>
    <h3 className="empty-state__title">No lesson plans found</h3>
    <p className="empty-state__description">
      Try adjusting your filters or search terms to find what you're looking for.
    </p>
    <button className="empty-state__button" onClick={onClearFilters}>
      Clear All Filters
    </button>
  </div>
);

const TopicsList = ({ setFilters = () => {} }) => {
  const topicsPerPage = 6;
  const [visibleCount, setVisibleCount] = useState(topicsPerPage);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedGrammar, setSelectedGrammar] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const sentinelRef = useRef(null);
  const hasMoreRef = useRef(false);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => prev + topicsPerPage);
  }, []);

  const handleToggle = (value, state, setState) => {
    setState((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const clearAllFilters = () => {
    setSelectedTopics([]);
    setSelectedLevels([]);
    setSelectedGrammar([]);
    setSearchQuery("");
  };

  const hasActiveFilters = selectedTopics.length > 0 || selectedLevels.length > 0 || selectedGrammar.length > 0 || searchQuery;

  // Filter logic
  const filteredTopics = topics.filter((topic) => {
    const matchesTopic = selectedTopics.length === 0 || selectedTopics.includes(topic.category);
    const matchesLevel = selectedLevels.length === 0 || selectedLevels.includes(topic.level);
    const matchesGrammar = selectedGrammar.length === 0 || selectedGrammar.includes(topic.languagepoint);
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = query === "" || topic.title.toLowerCase().includes(query) || topic.description.toLowerCase().includes(query);
    return matchesTopic && matchesLevel && matchesGrammar && matchesSearch;
  });

  const currentTopics = filteredTopics.slice(0, visibleCount);

  useEffect(() => {
    setVisibleCount(topicsPerPage);
  }, [selectedTopics, selectedLevels, selectedGrammar, searchQuery]);

  const hasMore = visibleCount < filteredTopics.length;
  hasMoreRef.current = hasMore;

  // Automatically load more topics as the user scrolls down
  useEffect(() => {
    if (!hasMore) return;

    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current) {
          loadMore();
        }
      },
      { root: null, rootMargin: "400px" }
    );

    observer.observe(node);

    // Fill the viewport if the remaining content does not create a scrollbar
    const fillViewport = () => {
      if (hasMoreRef.current && node.getBoundingClientRect().top < window.innerHeight) {
        loadMore();
        requestAnimationFrame(fillViewport);
      }
    };
    fillViewport();

    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  // Extract unique values for filters
  const grammarOptions = [...new Set(topics.map(t => t.languagepoint))].filter(Boolean).sort();
  const categoryOptions = ["Business", "General", "Food", "Health", "Travel", "Environment", "Education", "Holidays", "Airtraffic", "Tech", "Money", "AI", "Listening"];
  const levelOptions = ["A2", "B1", "B2", "C1"];

  return (
    <div className="topics-main-container">
      <SEO
        title="Free English Lesson Plans and Exercises"
        description="Browse free English lesson plans by category. Each lesson plan includes discussion questions, grammar, vocabulary and reading practice for teachers and students."
        name={SITE_NAME}
        type="article"
      />
      <div className="topics-container">
        {/* Page Header */}
        <header className="page-header">
          <h1 className="page-header__title">English Lesson Plans</h1>
          <p className="page-header__subtitle">
            Browse speaking lesson plans organized by category, level, and grammar focus.
          </p>
        </header>

        {/* Filter Bar */}
        <div className="filter-bar" role="search" aria-label="Filter lesson plans">
          <div className="filter-bar__search">
            <label htmlFor="topic-search" className="visually-hidden">Search lesson plans</label>
            <div className="filter-bar__input-wrapper">
              <FaSearch className="filter-bar__search-icon" aria-hidden="true" />
              <input
                id="topic-search"
                type="search"
                className="filter-bar__input"
                placeholder="Search lesson plans..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-describedby="search-hint"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="filter-bar__clear-search"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <FaTimes aria-hidden="true" />
                </button>
              )}
            </div>
            <span id="search-hint" className="visually-hidden">
              Type to filter lesson plans by title or description
            </span>
          </div>

          <div className="filter-bar__selects">
            <FilterSelect
              label="Category"
              options={categoryOptions}
              value={selectedTopics}
              onChange={(v) => handleToggle(v, selectedTopics, setSelectedTopics)}
              placeholder="All Categories"
            />
            <FilterSelect
              label="Level"
              options={levelOptions}
              value={selectedLevels}
              onChange={(v) => handleToggle(v, selectedLevels, setSelectedLevels)}
              placeholder="All Levels"
            />
            <FilterSelect
              label="Grammar"
              options={grammarOptions}
              value={selectedGrammar}
              onChange={(v) => handleToggle(v, selectedGrammar, setSelectedGrammar)}
              placeholder="All Grammar"
            />
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              className="filter-bar__clear-all"
              onClick={clearAllFilters}
            >
              <FaTimes aria-hidden="true" />
              Clear All
            </button>
          )}
        </div>

        {/* Results Count */}
        <p className="results-count" aria-live="polite">
           Showing <strong>{currentTopics.length}</strong> of <strong>{filteredTopics.length}</strong> lesson plans
        </p>

        {/* Topics Grid */}
        <ul className="topics-grid" role="list">
          {currentTopics.length > 0 ? (
            currentTopics.map((topic) => <TopicCard key={topic.id} topic={topic} />)
          ) : (
            <EmptyState onClearFilters={clearAllFilters} />
          )}
        </ul>

        {/* Infinite scroll trigger */}
        {hasMore && (
          <div className="load-more-container">
            <div ref={sentinelRef} className="load-more-sentinel" aria-hidden="true" />
            <p className="load-more-status" role="status" aria-live="polite">
              Loading more lesson plans...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopicsList;


